import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    Box,
    Paper,
    Typography,
    Slider,
    Divider,
    Button,
    FormControl,
    Select,
    InputLabel,
    MenuItem,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { loadSettings, saveSettings, defaultSettings } from '../services/settingsService';

export default function ScannerSettingsPage() {
    const [settings, setSettings] = useState(defaultSettings);
    const [devices, setDevices] = useState([]);
    const videoRef = useRef(null);
    const previewCanvasRef = useRef(null);
    const streamRef = useRef(null);

    useEffect(() => {
        setSettings(loadSettings());
        navigator.mediaDevices.enumerateDevices().then((allDevices) => {
            const videoInputs = allDevices.filter(d => d.kind === 'videoinput');
            setDevices(videoInputs);
        });
    }, []);

    useEffect(() => {
        const constraints = settings.camera.deviceId
            ? { video: { deviceId: { exact: settings.camera.deviceId } } }
            : { video: { facingMode: 'environment' } };

        navigator.mediaDevices.getUserMedia(constraints).then((stream) => {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((track) => track.stop());
            }
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.onloadedmetadata = () => {
                    videoRef.current.play();
                };
                videoRef.current.srcObject = stream;
            }
            requestAnimationFrame(drawPreview);
        });

        return () => {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((track) => track.stop());
            }
        };
    }, [settings.camera.deviceId, settings.cropping]);

    function drawPreview() {
        const video = videoRef.current;
        const canvas = previewCanvasRef.current;
        if (!video || !canvas) return;

        const ctx = canvas.getContext('2d');
        const vw = video.videoWidth;
        const vh = video.videoHeight;

        if (!vw || !vh) {
            requestAnimationFrame(drawPreview);
            return;
        }

        canvas.width = vh;
        canvas.height = vw;

        ctx.save();
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        ctx.translate(canvas.width, 0);
        ctx.rotate(Math.PI / 2);

        ctx.drawImage(video, 0, 0);

        const { leftCropPercent, rightCropPercent, topCropPercent, bottomCropPercent } = settings.cropping;
        const cropLeft = leftCropPercent * vw;
        const cropRight = rightCropPercent * vw;
        const cropTop = topCropPercent * vh;
        const cropBottom = bottomCropPercent * vh;

        const cropWidth = vw - cropLeft - cropRight;
        const cropHeight = vh - cropTop - cropBottom;

        ctx.strokeStyle = '#C0FF00';
        ctx.lineWidth = 4;
        ctx.strokeRect(cropLeft, cropTop, cropWidth, cropHeight);

        ctx.restore();
        requestAnimationFrame(drawPreview);
    }

    const handleCropChange = (key) => (_, val) => {
        setSettings((prev) => ({
            ...prev,
            cropping: { ...prev.cropping, [key]: val / 100 },
        }));
    };

    const handleThreshChange = (key) => (_, val) => {
        setSettings((prev) => ({
            ...prev,
            thresholds: { ...prev.thresholds, [key]: val / 100 },
        }));
    };

    const handleSave = () => {
        saveSettings(settings);
        alert('Einstellungen gespeichert');
    };

    const handleReset = () => {
        setSettings(defaultSettings);
    };

    return (
        <Box sx={{ maxWidth: 600, mx: 'auto', p: 3 }}>
            <Typography variant="h4" gutterBottom>
                Scanner‑Einstellungen
            </Typography>

            <Paper sx={{ p: 3, mb: 4 }}>
                <Typography variant="h6" gutterBottom>
                    Live‑Vorschau
                </Typography>
                <Box
                    sx={{
                        width: '100%',
                        background: '#000',
                        borderRadius: 2,
                        overflow: 'hidden',
                    }}
                >
                    <canvas ref={previewCanvasRef} style={{ width: '100%', display: 'block' }} />
                    <video ref={videoRef} style={{ display: 'none' }} muted playsInline />
                </Box>
            </Paper>

            <Paper sx={{ p: 3, mb: 4 }}>
                <Typography variant="h6">Cropping (alle Seiten)</Typography>
                <Divider sx={{ my: 2 }} />

                <Typography gutterBottom>Oberer Rand (%)</Typography>
                <Slider
                    value={settings.cropping.leftCropPercent * 100}
                    onChange={handleCropChange('leftCropPercent')}
                    step={1}
                    min={0}
                    max={50}
                    valueLabelDisplay="auto"
                />

                <Typography gutterBottom sx={{ mt: 3 }}>
                    Unterer Rand (%)
                </Typography>
                <Slider
                    value={settings.cropping.rightCropPercent * 100}
                    onChange={handleCropChange('rightCropPercent')}
                    step={1}
                    min={0}
                    max={50}
                    valueLabelDisplay="auto"
                />

                <Typography gutterBottom sx={{ mt: 3 }}>
                    Rechter Rand (%)
                </Typography>
                <Slider
                    value={settings.cropping.topCropPercent * 100}
                    onChange={handleCropChange('topCropPercent')}
                    step={1}
                    min={0}
                    max={50}
                    valueLabelDisplay="auto"
                />

                <Typography gutterBottom sx={{ mt: 3 }}>
                    Linker Rand (%)
                </Typography>
                <Slider
                    value={settings.cropping.bottomCropPercent * 100}
                    onChange={handleCropChange('bottomCropPercent')}
                    step={1}
                    min={0}
                    max={50}
                    valueLabelDisplay="auto"
                />
            </Paper>

            <Paper sx={{ p: 3 }}>
                <Typography variant="h6">Schwellwerte (Faktor × Max)</Typography>
                <Divider sx={{ my: 2 }} />

                <Typography gutterBottom>Horizontal</Typography>
                <Slider
                    value={settings.thresholds.horizontalFactor * 100}
                    onChange={handleThreshChange('horizontalFactor')}
                    step={1}
                    min={20}
                    max={100}
                    valueLabelDisplay="auto"
                />

                <Typography gutterBottom sx={{ mt: 3 }}>
                    Vertikal
                </Typography>
                <Slider
                    value={settings.thresholds.verticalFactor * 100}
                    onChange={handleThreshChange('verticalFactor')}
                    step={1}
                    min={20}
                    max={100}
                    valueLabelDisplay="auto"
                />
            </Paper>

            <FormControl fullWidth sx={{ mt: 3 }}>
                <InputLabel id="camera-label">Kamera</InputLabel>
                <Select
                    labelId="camera-label"
                    value={settings.camera.deviceId || ''}
                    label="Kamera"
                    onChange={(e) =>
                        setSettings((prev) => ({
                            ...prev,
                            camera: { ...prev.camera, deviceId: e.target.value }
                        }))
                    }
                >
                    {devices.map((device) => (
                        <MenuItem key={device.deviceId} value={device.deviceId}>
                            {device.label || `Kamera ${device.deviceId}`}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
                <Button variant="contained" onClick={handleSave}>
                    Speichern
                </Button>
                <Button variant="outlined" onClick={handleReset}>
                    Zurücksetzen
                </Button>
            </Box>
        </Box>
    );
}
